import React from 'react';
import { useInView } from 'react-intersection-observer';
import { Globe, Bot, Megaphone, Target, Sparkles, ArrowRight } from 'lucide-react';
import BackgroundAnimation from '../components/BackgroundAnimation';

const Services = () => {
  const { ref: servicesRef, inView: servicesInView } = useInView({ threshold: 0.1, triggerOnce: true });

  const services = [
    {
      icon: Globe,
      title: 'Digital Web Solutions',
      description: 'Bespoke web experiences crafted to transform visitors into loyal customers through intuitive design.',
      emoji: '🌐'
    },
    {
      icon: Bot,
      title: 'Intelligent Process Automation',
      description: 'Harness AI-driven workflows to eliminate manual bottlenecks and amplify operational efficiency.',
      emoji: '🤖'
    },
    {
      icon: Megaphone,
      title: 'Social Presence Amplification',
      description: 'Cultivate authentic connections and expand your digital footprint across every major platform.',
      emoji: '📣'
    },
    {
      icon: Target,
      title: 'Revenue-Focused Lead Gen',
      description: 'Deploy precision-targeted campaigns that attract decision-makers and optimize your marketing investment.',
      emoji: '🎯'
    },
    {
      icon: Sparkles,
      title: 'Brand Identity Evolution',
      description: 'Forge compelling visual narratives that differentiate your business and command attention.',
      emoji: '✨'
    }
  ];

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', paddingTop: '80px', position: 'relative' }}>
      <BackgroundAnimation />
      {/* Hero */}
      <section className="py-24" style={{ padding: '120px 7.6923% 80px', background: 'var(--bg-primary)' }}>
        <div className="max-w-5xl mx-auto text-center">
          <div className="body-medium mb-2" style={{ color: 'var(--brand-primary)' }}>
            What We Deliver
          </div>
          <h1 className="display-huge mb-6" style={{ color: 'var(--text-primary)' }}>
            Tailored Strategies for
            <span className="block" style={{ color: 'var(--brand-primary)' }}>
              Exponential Growth
            </span>
          </h1>
          <p className="body-large" style={{ color: 'var(--text-secondary)', maxWidth: '700px', margin: '0 auto' }}>
            Integrated digital capabilities engineered to unlock new revenue streams and maximize enterprise potential
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section
        ref={servicesRef}
        className="py-12"
        style={{ padding: '60px 7.6923% 100px', background: 'var(--bg-primary)' }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  className={`p-8 rounded-lg dark-hover card-lift ${servicesInView ? 'fade-in' : 'opacity-0'}`}
                  style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid var(--border-subtle)',
                    animationDelay: `${index * 0.1}s`
                  }}
                >
                  <div className="flex items-center gap-3 mb-6">
                    <div
                      className="w-14 h-14 rounded-lg flex items-center justify-center"
                      style={{ background: 'rgba(0, 255, 209, 0.1)' }}
                    >
                      <Icon size={28} style={{ color: 'var(--brand-primary)' }} />
                    </div>
                    <span className="text-3xl">{service.emoji}</span>
                  </div>
                  <h3 className="heading-2 mb-4" style={{ color: 'var(--text-primary)' }}>
                    {service.title}
                  </h3>
                  <p className="body-medium mb-6" style={{ color: 'var(--text-secondary)' }}>
                    {service.description}
                  </p>
                  <a
                    href="https://calendly.com/americanprimeforyou"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 body-medium dark-transition hover:opacity-80"
                    style={{ color: 'var(--brand-primary)', textDecoration: 'none' }}
                  >
                    <span>Book a Call for Quote</span>
                    <ArrowRight size={18} />
                  </a>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="py-24"
        style={{
          padding: '100px 7.6923%',
          background: 'linear-gradient(135deg, rgba(0, 255, 209, 0.05) 0%, rgba(0, 255, 209, 0.02) 100%)',
          borderTop: '1px solid rgba(0, 255, 209, 0.1)',
          borderBottom: '1px solid rgba(0, 255, 209, 0.1)'
        }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>
            Prepared to Accelerate
            <span className="block" style={{ color: 'var(--brand-primary)' }}>
              Your Success?
            </span>
          </h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Let's explore how our solutions can propel you toward your ambitions
          </p>
          <a
            href="https://calendly.com/americanprimeforyou"
            target="_blank"
            rel="noopener noreferrer"
            className="button-text dark-button-animate inline-flex items-center space-x-2"
            style={{
              background: 'var(--brand-primary)',
              color: '#000000',
              padding: '14px 32px',
              borderRadius: '0px',
              textDecoration: 'none'
            }}
          >
            <span>Book a Free Strategy Call</span>
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Services;